import { getLanguage as getInterfaceLanguage } from '@/utils/i18n';
import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useState, useMemo } from 'react';
import {
  Filter, Eye, Play, MousePointerClick, ShoppingCart,
  Monitor, SlidersHorizontal,
} from 'lucide-react';
import {
  Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer,
  Tooltip as RechartsTooltip, Cell, Line, ComposedChart,
} from 'recharts';
import { cn } from '@/lib/utils';
import type { MetricsSummary, RetentionTimeline } from '../../../metrics/hooks/useMetricsDataEnhanced';
import type { VideoPitch } from '@/types/pitch';
import { isNearAnyPitch } from '@/utils/pitches';

// ─── Types ────────────────────────────────────────────────────────────────────

interface FunnelStep {
  id: string;
  label: string;
  value: number;
  percentage: number;
  icon: React.ElementType;
  tooltipTitle: string;
  tooltipDesc: string;
}

interface RetentionPoint {
  label: string;      // "M:SS"
  timeSeconds: number;
  viewers: number;
  retention: number;
  isPitch?: boolean;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatNumber(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1).replace('.0', '')}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1).replace('.0', '')}K`;
  return n.toLocaleString(getInterfaceLanguage());
}

function formatSeconds(s: number): string {
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, '0')}`;
}

/** Reduz a série de retenção para no máximo `maxPoints` pontos uniformes */
function downsampleRetention(timeline: RetentionTimeline[], maxPoints = 20): RetentionTimeline[] {
  if (timeline.length <= maxPoints) return timeline;
  const step = Math.ceil(timeline.length / maxPoints);
  return timeline.filter((_, i) => i % step === 0 || i === timeline.length - 1);
}

// ─── Paleta ───────────────────────────────────────────────────────────────────

const BLUE_PRIMARY   = '#2D5BFF';
const BLUE_SECONDARY = '#3E73FF';
const BLUE_LIGHT     = '#5C8DFF';
const BLUE_SOFT      = '#7AA7FF';
const BLUE_PALE      = '#9BBEFF';
const BAR_COLORS     = [BLUE_PRIMARY, BLUE_SECONDARY, BLUE_LIGHT, BLUE_SOFT, BLUE_PALE];
const GREEN_PREMIUM  = '#0D9668';

// ─── Sub-components ───────────────────────────────────────────────────────────

function FunnelBar({
  step, index, maxValue,
}: { step: FunnelStep; index: number; maxValue: number }) {
  const _interfaceLanguage = useLanguage();
  const barWidth = Math.max(10, (step.value / maxValue) * 100);
  const Icon = step.icon;
  const color = BAR_COLORS[index] ?? BAR_COLORS[BAR_COLORS.length - 1];
  const [hovered, setHovered] = useState(false);

  return (
    <div
      className="relative flex items-center gap-5 group cursor-pointer py-1.5"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Icon + Label */}
      <div className="flex items-center gap-3.5 min-w-[190px] lg:min-w-[210px]">
        <div
          className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: `${color}14` }}
        >
          <Icon className="w-[15px] h-[15px]" style={{ color, strokeWidth: 1.8 }} />
        </div>
        <p className="text-[13.5px] font-medium text-foreground tracking-[-0.01em]">{step.label}</p>
      </div>

      {/* Bar + Value */}
      <div className="flex-1 flex items-center gap-4 relative">
        <div className="flex-1 h-[36px] rounded-[12px] bg-muted/60 relative overflow-hidden">
          <div
            className="h-full rounded-[12px] flex items-center px-4 transition-all duration-700 ease-out group-hover:brightness-[1.04]"
            style={{ width: `${barWidth}%`, backgroundColor: color, minWidth: '56px' }}
          >
            <span className="text-white text-[13.5px] font-bold leading-none tracking-tight">
              {formatNumber(step.value)}
            </span>
          </div>
        </div>

        {/* Percentage */}
        <span className={cn(
          'min-w-[50px] text-right text-[12.5px] font-semibold tabular-nums',
          index === 0
            ? 'text-muted-foreground'
            : step.percentage >= 20
              ? 'text-blue-600 dark:text-blue-400'
              : 'text-red-500',
        )}>
          {step.percentage}%
        </span>

        {/* Tooltip */}
        {hovered && (
          <div
            className="absolute z-50 pointer-events-none"
            style={{
              bottom: 'calc(100% + 10px)',
              left: `calc(${Math.min(barWidth, 80) / 2}% + 95px)`,
              transform: 'translateX(-50%)',
            }}
          >
            <div className="bg-background rounded-xl border border-border shadow-[0_8px_30px_rgba(0,0,0,0.08)] p-4 min-w-[240px]">
              <p className="text-[13px] font-semibold text-foreground mb-2">{step.tooltipTitle}</p>
              <p className="text-[12px] text-muted-foreground whitespace-pre-line leading-[1.6]">{step.tooltipDesc}</p>
            </div>
            <div className="flex justify-center -mt-[1px]">
              <div className="w-2.5 h-2.5 bg-background border-r border-b border-border rotate-45 -translate-y-[5px]" />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function VisualFunnelView({ steps }: { steps: FunnelStep[] }) {
  const _interfaceLanguage = useLanguage();
  const maxValue = Math.max(...steps.map(s => s.value));
  const [animated, setAnimated] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  React.useEffect(() => {
    const t = setTimeout(() => setAnimated(true), 50);
    return () => clearTimeout(t);
  }, []);

  return (
    <div className="flex items-end gap-3 px-2" style={{ height: '320px' }}>
      {steps.map((step, index) => {
        const Icon = step.icon;
        const color = BAR_COLORS[index];
        const barHeight = Math.max(5, (step.value / maxValue) * 100);
        const isHovered = hoveredIndex === index;
        const isFaded = hoveredIndex !== null && hoveredIndex !== index;

        return (
          <div
            key={step.id}
            className="flex-1 flex flex-col items-center relative"
            style={{ height: '100%' }}
          >
            {/* Value + percentage above bar */}
            <div
              className="text-center mb-2 mt-auto"
              style={{ marginBottom: '8px', opacity: isFaded ? 0.4 : 1, transition: 'opacity 200ms ease' }}
            >
              <p className="text-[11px] font-semibold text-muted-foreground tabular-nums">{step.percentage}%</p>
              <p className="text-[12px] font-bold text-foreground">{formatNumber(step.value)}</p>
            </div>

            {/* Column */}
            <div
              className="w-full rounded-t-[6px] cursor-pointer relative"
              style={{
                height: animated ? `${barHeight}%` : '0%',
                backgroundColor: color,
                opacity: isFaded ? 0.3 : 1,
                transitionProperty: 'height, opacity',
                transitionDuration: '600ms, 200ms',
                transitionTimingFunction: 'cubic-bezier(0.25,0.1,0.25,1), ease',
                maxHeight: '220px',
              }}
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
            >
              {isHovered && (
                <div className="absolute bottom-full mb-3 left-1/2 -translate-x-1/2 z-50 pointer-events-none">
                  <div className="bg-background rounded-lg border border-border shadow-[0_4px_16px_rgba(0,0,0,0.10)] px-3 py-2.5 w-[180px]">
                    <p className="text-[12px] font-semibold text-foreground mb-0.5 leading-tight">{step.tooltipTitle}</p>
                    <p className="text-[11px] text-muted-foreground leading-[1.4]">{step.tooltipDesc}</p>
                  </div>
                  <div className="flex justify-center">
                    <div className="w-2 h-2 bg-background border-r border-b border-border rotate-45 -translate-y-[5px]" />
                  </div>
                </div>
              )}
            </div>

            {/* Label */}
            <div
              className="flex flex-col items-center gap-1.5 mt-3"
              style={{ opacity: isFaded ? 0.4 : 1, transition: 'opacity 200ms ease' }}
            >
              <Icon className="w-4 h-4" style={{ color, strokeWidth: 1.8 }} />
              <span className="text-[10.5px] font-medium text-muted-foreground text-center leading-tight">{step.label}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

interface FunnelTabContentProps {
  metricsEnhanced: any;
}

export function FunnelTabContent({ metricsEnhanced }: FunnelTabContentProps) {
  const _interfaceLanguage = useLanguage();
  const summary: MetricsSummary | null = metricsEnhanced?.summary ?? null;
  const retentionTimeline: RetentionTimeline[] = metricsEnhanced?.retentionTimeline ?? [];
  const videoInfo = metricsEnhanced?.videoInfo ?? null;
  const isLoading: boolean = metricsEnhanced?.isLoading ?? false;

  // ── Funnel steps ────────────────────────────────────────────────────────────

  const steps = useMemo<FunnelStep[]>(() => {
    if (!summary) return [];

    const vt = summary.views_total   ?? 0;
    const vu = summary.views_unique  ?? 0;
    const pu = summary.plays_unique  ?? 0;
    const cc = summary.cta_clicks    ?? 0;
    const cv = summary.conversions   ?? 0;

    const pct = (num: number, den: number) =>
      den > 0 ? parseFloat(((num / den) * 100).toFixed(1)) : 0;

    return [
      {
        id: 'views_total',
        label: translateInterfaceText("Visualizações Totais"),
        value: vt,
        percentage: 100,
        icon: Monitor,
        tooltipTitle: translateInterfaceText("Visualizações Totais"),
        tooltipDesc: `${formatNumber(vt)} visualizações no período selecionado.\nRepresenta o total de vezes que o player foi carregado.`,
      },
      {
        id: 'views_unique',
        label: translateInterfaceText("Visualizações Únicas"),
        value: vu,
        percentage: pct(vu, vt),
        icon: Eye,
        tooltipTitle: translateInterfaceText("Visualizações Únicas"),
        tooltipDesc: `${formatNumber(vu)} visitantes únicos visualizaram o vídeo.\n${pct(vu, vt)}% do total de visualizações.`,
      },
      {
        id: 'plays_unique',
        label: translateInterfaceText("Plays Únicos"),
        value: pu,
        percentage: pct(pu, vu),
        icon: Play,
        tooltipTitle: translateInterfaceText("Plays Únicos"),
        tooltipDesc: `${formatNumber(pu)} usuários iniciaram o vídeo.\n${pct(pu, vu)}% das visualizações únicas.`,
      },
      {
        id: 'cta_clicks',
        label: translateInterfaceText("Cliques no CTA"),
        value: cc,
        percentage: pct(cc, pu),
        icon: MousePointerClick,
        tooltipTitle: translateInterfaceText("Cliques no CTA"),
        tooltipDesc: `${formatNumber(cc)} cliques no botão de ação.\n${pct(cc, pu)}% dos plays únicos.`,
      },
      {
        id: 'conversions',
        label: translateInterfaceText("Conversões"),
        value: cv,
        percentage: pct(cv, cc),
        icon: ShoppingCart,
        tooltipTitle: translateInterfaceText("Conversões"),
        tooltipDesc: `${formatNumber(cv)} conversões confirmadas.\n${pct(cv, cc)}% dos cliques no CTA.`,
      },
    ];
  }, [summary, _interfaceLanguage]);

  // ── Rates ───────────────────────────────────────────────────────────────────

  const rates = useMemo(() => {
    if (!summary) return [];
    const pu = summary.plays_unique  ?? 0;
    const vu = summary.views_unique  ?? 0;
    const cc = summary.cta_clicks    ?? 0;
    const cv = summary.conversions   ?? 0;

    const pct = (n: number, d: number) =>
      d > 0 ? parseFloat(((n / d) * 100).toFixed(1)) : 0;

    return [
      { label: translateInterfaceText("Play Rate"),        value: parseFloat((summary.play_rate ?? pct(pu, vu)).toFixed(1)) },
      { label: translateInterfaceText("Click Rate"),       value: pct(cc, pu) },
      { label: translateInterfaceText("Conversion Rate"),  value: parseFloat((summary.conversion_rate ?? pct(cv, cc)).toFixed(1)) },
    ];
  }, [summary, _interfaceLanguage]);

  // ── Retention / pitch chart ──────────────────────────────────────────────────

  // Início de cada pitch, em segundos — a barra a ±15s de qualquer um fica dourada.
  const pitchSeconds = useMemo<number[]>(
    () => ((videoInfo?.pitches ?? []) as VideoPitch[]).map((p) => p.seconds),
    [videoInfo],
  );

  const chartData = useMemo<RetentionPoint[]>(() => {
    const sampled = downsampleRetention(retentionTimeline, 20);
    return sampled.map(p => ({
      label:       formatSeconds(p.time_seconds),
      timeSeconds: p.time_seconds,
      viewers:     p.viewers_count,
      retention:   parseFloat(p.retention_pct.toFixed(1)),
      isPitch:     isNearAnyPitch(p.time_seconds, pitchSeconds),
    }));
  }, [retentionTimeline, pitchSeconds]);

  // Top 3 pontos com mais viewers (momento de pico de audiência)
  const topMoments = useMemo(() =>
    [...chartData]
      .sort((a, b) => b.viewers - a.viewers)
      .slice(0, 3),
    [chartData],
  );

  const momentLabels = [translateInterfaceText("Melhor momento"), translateInterfaceText("Segundo melhor momento"), translateInterfaceText("Terceiro melhor momento")];

  // ── Estado vazio ─────────────────────────────────────────────────────────────

  const hasData = !!summary && (summary.views_total ?? 0) > 0;

  if (isLoading) {
    return (
      <div className="space-y-8">
        {[1, 2].map(i => (
          <div key={i} className="rounded-2xl border border-border h-64 animate-pulse bg-muted/40" />
        ))}
      </div>
    );
  }

  if (!hasData) {
    return (
      <div className="bg-background rounded-2xl border border-border p-16 text-center">
        <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-5">
          <Filter className="w-6 h-6 text-primary" />
        </div>
        <h3 className="text-[18px] font-semibold text-foreground mb-2">
          {translateInterfaceText("Nenhum dado de funil disponível ainda")}</h3>
        <p className="text-[13px] text-muted-foreground max-w-md mx-auto leading-relaxed">
          {translateInterfaceText("Selecione um vídeo e um período com visualizações para ver o funil de conversão.")}</p>
      </div>
    );
  }

  const maxValue = Math.max(...steps.map(s => s.value));

  return (
    <div className="space-y-8">

      {/* ── 1. Funnel Card ────────────────────────────────────────────────────── */}
      <div className="rounded-2xl border border-border shadow-[0_1px_3px_rgba(0,0,0,0.02)] bg-background">
        {/* Rates header */}
        <div className="px-7 lg:px-9 pt-7 pb-4 flex items-center justify-end">
          <div className="flex items-center gap-6">
            {rates.map((rate, i) => (
              <React.Fragment key={rate.label}>
                {i > 0 && <div className="w-px h-4 bg-border" />}
                <div className="flex items-center gap-2">
                  <span className="text-[11px] text-muted-foreground font-medium">{rate.label}</span>
                  <span className="text-[14px] font-bold text-foreground tabular-nums">{rate.value}%</span>
                </div>
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Visual funnel */}
        <div className="px-7 lg:px-9 pb-8">
          <VisualFunnelView steps={steps} />
        </div>

      </div>

      {/* ── 2. Retenção & Pitch Chart ─────────────────────────────────────────── */}
      {chartData.length > 0 && (
        <div className="rounded-2xl border border-border shadow-[0_1px_3px_rgba(0,0,0,0.02)] bg-background overflow-hidden">
          <div className="px-7 py-6 border-b border-border">
            <h3 className="text-[16px] font-semibold text-foreground tracking-[-0.01em]">
              {translateInterfaceText("Retenção ao Longo do Vídeo")}</h3>
            <p className="text-[12px] text-muted-foreground mt-1 font-normal">
              {translateInterfaceText("Veja em que ponto do vídeo a audiência se engaja e onde ocorre a maior queda.")}</p>
          </div>

          <div className="px-7 py-7">
            {/* Legend */}
            <div className="flex items-center gap-6 mb-6">
              <div className="flex items-center gap-2">
                <div className="w-2.5 h-2.5 rounded-[3px] bg-[#1D4ED8]" />
                <span className="text-[11px] text-muted-foreground font-medium">{translateInterfaceText("Espectadores")}</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-5 h-[1.5px] bg-muted-foreground/40 rounded-full" />
                <span className="text-[11px] text-muted-foreground font-medium">{translateInterfaceText("Retenção (%)")}</span>
              </div>
              {pitchSeconds.length > 0 && (
                <div className="flex items-center gap-2">
                  <div className="w-2.5 h-2.5 rounded-[3px] bg-[#C9A24D]" />
                  <span className="text-[11px] text-muted-foreground font-medium">{translateInterfaceText("Ponto do pitch")}</span>
                </div>
              )}
            </div>

            <ResponsiveContainer width="100%" height={270}>
              <ComposedChart data={chartData} barCategoryGap="30%">
                <CartesianGrid strokeDasharray="4 4" stroke="rgba(0,0,0,0.05)" vertical={false} />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 10.5, fill: '#A0A8B4', fontWeight: 500 }}
                  axisLine={{ stroke: 'rgba(0,0,0,0.06)' }}
                  tickLine={false}
                />
                <YAxis
                  yAxisId="viewers"
                  tick={{ fontSize: 10.5, fill: '#A0A8B4', fontWeight: 500 }}
                  axisLine={false}
                  tickLine={false}
                  width={36}
                  tickFormatter={(v: number) => formatNumber(v)}
                />
                <YAxis
                  yAxisId="retention"
                  orientation="right"
                  tick={{ fontSize: 10.5, fill: '#A0A8B4', fontWeight: 500 }}
                  axisLine={false}
                  tickLine={false}
                  width={34}
                  tickFormatter={(v: number) => `${v}%`}
                  domain={[0, 100]}
                />
                <RechartsTooltip
                  contentStyle={{
                    backgroundColor: '#ffffff',
                    border: '1px solid #e2e8f0',
                    borderRadius: '12px',
                    padding: '14px 18px',
                    fontSize: '12px',
                    boxShadow: '0 8px 30px rgba(0,0,0,0.08)',
                    lineHeight: '1.6',
                  }}
                  formatter={(value: number, name: string) => {
                    const labels: Record<string, string> = {
                      viewers:   'Espectadores',
                      retention: 'Retenção',
                    };
                    const suffix = name === 'retention' ? '%' : '';
                    return [`${formatNumber(value)}${suffix}`, labels[name] ?? name];
                  }}
                  labelFormatter={(label) => `Momento ${label}`}
                />
                <Bar yAxisId="viewers" dataKey="viewers" radius={[4, 4, 0, 0]} maxBarSize={18}>
                  {chartData.map((entry, index) => (
                    <Cell
                      key={index}
                      fill={entry.isPitch ? '#C9A24D' : BLUE_PRIMARY}
                      opacity={entry.isPitch ? 0.85 : 0.80}
                    />
                  ))}
                </Bar>
                <Line
                  yAxisId="retention"
                  dataKey="retention"
                  type="monotone"
                  stroke="#A0A8B4"
                  strokeWidth={1.5}
                  dot={false}
                  strokeDasharray="6 4"
                />
              </ComposedChart>
            </ResponsiveContainer>

            {/* Top 3 moments */}
            {topMoments.length > 0 && (
              <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
                {topMoments.map((d, i) => (
                  <div
                    key={i}
                    className="rounded-2xl bg-muted/30 border border-border p-6 hover:shadow-[0_2px_8px_rgba(0,0,0,0.04)] transition-shadow duration-300"
                  >
                    <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-[0.06em]">
                      {momentLabels[i]}
                    </span>
                    <p className="text-[24px] font-bold text-foreground mt-2.5 tracking-[-0.02em] leading-none">
                      {d.label}
                    </p>
                    <p className="text-[11.5px] text-muted-foreground mt-3 font-medium">
                      {formatNumber(d.viewers)} {" "}{translateInterfaceText("espectadores ·")}{" "}{d.retention}{translateInterfaceText("% retenção")}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

    </div>
  );
}
